<script setup>
import { computed, useAttrs, useSlots, useTemplateRef } from "vue";

import BaseSpinner from "./BaseSpinner.vue";
import SvgIcon from "./SvgIcon.vue";
import { useAsyncHandler } from "./useAsyncHandler";

const props = defineProps({
  as: {
    type: String,
    default: "button",
    validator: (v) => ["button", "a", "div"].includes(v),
  },
  variant: {
    type: String,
    default: "filled",
    validator: (v) => ["filled", "outlined", "ghost", "text"].includes(v),
  },
  size: {
    type: String,
    default: "medium",
    validator: (v) => ["xsmall", "small", "medium"].includes(v),
  },
  color: {
    type: String,
    default: "primary",
    validator: (v) => ["primary", "grey", "destructive"].includes(v),
  },
  fullWidth: { type: Boolean, default: false },
  iconBefore: { type: String, default: undefined },
  iconAfter: { type: String, default: undefined },
  disabled: { type: Boolean, default: false },
  activated: { type: Boolean, default: undefined },
  // Externally-driven loading state. Combines with the implicit
  // useAsyncHandler tracking so the spinner shows for both auto-tracked
  // async @click handlers and consumer-managed flows.
  loading: { type: Boolean, default: false },
});

defineOptions({ inheritAttrs: false });
const $attrs = useAttrs();
const slots = useSlots();

const buttonRef = useTemplateRef("button");
defineExpose({
  blur: () => void buttonRef.value?.blur(),
  focus: () => void buttonRef.value?.focus(),
  getBoundingClientRect: () => buttonRef.value?.getBoundingClientRect(),
});

const { attrs, processing } = useAsyncHandler(100, "onClick");
const showSpinner = computed(() => props.loading || processing.value.onClick);
// Disable the button when caller explicitly sets `disabled` or when the button is loading
const isDisabled = computed(() => props.disabled || showSpinner.value);
// Icon-only buttons have no `.content` wrapper to host the existing
// in-content spinner — render a button-level spinner instead and hide
// the icons in its place.
const iconOnlyLoading = computed(() => showSpinner.value && !slots.default);

const aria = computed(() => {
  if (props.as === "button" && props.activated !== undefined) {
    switch ($attrs.role) {
      case "radio":
        return {
          ariaChecked: props.activated ? "true" : "false",
          tabIndex: props.activated ? 0 : -1,
        };
      case "tab":
      case "option":
        return { ariaSelected: props.activated ? "true" : "false" };
      default:
        return { ariaPressed: props.activated ? "true" : "false" };
    }
  }
  return {};
});

const typeAttr = computed(() =>
  props.as === "button" && $attrs.type === undefined ? { type: "button" } : {},
);
</script>

<template>
  <component
    :is="as"
    ref="button"
    :class="['base-button', variant, color, size, { 'full-width': fullWidth, activated }]"
    :disabled="isDisabled"
    v-bind="{ ...typeAttr, ...attrs, ...aria }"
  >
    <span v-if="iconBefore" class="icon-before" :class="{ 'is-loading': iconOnlyLoading }">
      <SvgIcon :name="iconBefore" aria-hidden="true" />
    </span>
    <span v-if="$slots.default" class="content" :class="{ loading: showSpinner }">
      <span class="label"><slot /></span>
      <span v-if="showSpinner" class="spinner-wrap">
        <BaseSpinner size="20" />
      </span>
    </span>
    <span v-if="iconAfter" class="icon-after" :class="{ 'is-loading': iconOnlyLoading }">
      <SvgIcon :name="iconAfter" aria-hidden="true" />
    </span>
    <span v-if="iconOnlyLoading" class="spinner-wrap">
      <BaseSpinner size="20" />
    </span>
  </component>
</template>

<style scoped lang="scss">
@use "@/colors" as c;
@use "@/styles/mixins/typography";

.base-button {
  // Explicit reset so user-agent <button> styles never leak through.
  appearance: none;
  box-sizing: border-box;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  text-align: inherit;
  text-decoration: none;
  outline: revert;
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  user-select: none;
  font-weight: 500;
  cursor: pointer;
  flex-shrink: 0;

  .icon-before,
  .icon-after {
    display: block;
    flex-shrink: 0;
    width: 20px;
    height: 20px;

    &.is-loading {
      visibility: hidden;
    }
  }

  .content {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: inherit;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &.loading .label {
      visibility: hidden;
    }
  }

  .spinner-wrap {
    position: absolute;
    inset: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &.xsmall {
    font-size: 13px;
    gap: 4px;
    line-height: 16px;
    height: 24px;
    min-width: 24px;
    border-radius: 4px;

    .icon-before,
    .icon-after {
      width: 16px;
      height: 16px;
    }

    &:has(.content) {
      padding-inline: 6px;
    }
  }

  &.small {
    font-size: 14px;
    gap: 4px;
    line-height: 20px;
    height: 32px;
    min-width: 32px;
    border-radius: 4px;

    &:has(.content) {
      padding-inline: 8px;
    }
  }

  &.medium {
    font-size: 14px;
    gap: 4px;
    line-height: 20px;
    height: 36px;
    min-width: 36px;

    &:has(.content) {
      padding-inline: 12px;
    }
  }

  &.full-width {
    width: 100%;
  }

  &[class]:disabled {
    cursor: not-allowed;
  }

  &.filled {
    &[class]:disabled {
      color: c.$text-disabled;
      background-color: c.$background-grey-light;
    }

    &.primary {
      background-color: c.$main;
      color: c.$text-inverse;
      outline-offset: 3px;

      &:not(:disabled, .activated) {
        &:hover {
          background-color: c.$main-hover;
        }

        &:active {
          background-color: c.$main-hover;
        }
      }

      &.activated {
        background-color: c.$main;
      }
    }

    &.grey {
      background-color: c.$background-alpha-hover;
      color: c.$text-subtle;

      &:not(:disabled, .activated) {
        &:hover {
          background-color: c.$background-alpha-active;
          color: c.$text;
        }

        &:active {
          background-color: c.$background-alpha-pressed;
          color: c.$text;
        }
      }

      &.activated {
        background-color: c.$background-grey;
        color: c.$text;
      }
    }

    &.destructive {
      background-color: c.$destructive;
      color: c.$text-inverse;

      &:not(:disabled, .activated) {
        &:hover {
          background-color: c.$destructive-hover;
        }

        &:active {
          background-color: c.$destructive-active;
        }
      }

      &.activated {
        background-color: c.$destructive-active;
      }
    }
  }

  &.outlined {
    background-color: c.$background-ui;

    &[class]:disabled {
      color: c.$text-disabled;
      border: 1px solid c.$border;
    }

    &.primary {
      color: c.$text-subtle;
      border: 1px solid c.$main;

      &:not(:disabled, .activated) {
        &:hover {
          color: c.$main;
          background-color: c.$background-grey-light;
        }

        &:active {
          color: c.$main;
          background-color: c.$background-grey;
        }
      }

      &.activated {
        color: c.$main;
        background-color: c.$background-grey;
      }
    }

    &.grey {
      color: c.$text-subtle;
      background-color: c.$background-ui;
      border: 1px solid c.$border-subtle;

      &:not(:disabled, .activated) {
        &:hover {
          color: c.$text;
          background-color: c.$background-alpha-hover;
          border: 1px solid c.$border;
        }

        &:active {
          color: c.$text;
          background-color: c.$background-alpha-active;
          border: 1px solid c.$border;
        }
      }

      &.activated {
        color: c.$text-inverse;
        background-color: c.$main;
        border: 1px solid c.$main;
      }
    }

    &.destructive {
      color: c.$destructive;
      background-color: c.$background-ui;
      border: 1px solid currentcolor;

      &:not(:disabled, .activated) {
        &:hover {
          background-color: c.$destructive-light;
        }

        &:active {
          background-color: c.$destructive-light-hover;
        }
      }

      &.activated {
        background-color: c.$destructive-light-hover;
      }
    }
  }

  &.ghost {
    &[class]:disabled {
      cursor: not-allowed;
      color: c.$text-disabled;
    }

    &.primary {
      color: c.$main;

      &:not(:disabled, .activated) {
        &:hover {
          background-color: c.$background-alpha-hover;
        }

        &:active {
          background-color: c.$background-alpha-active;
        }
      }

      &.activated {
        background-color: c.$background-grey;
      }
    }

    &.grey {
      color: c.$text-subtle;

      &:not(:disabled, .activated) {
        &:hover {
          color: c.$text;
          background-color: c.$background-alpha-hover;
        }

        &:active {
          color: c.$text;
          background-color: c.$background-alpha-active;
        }
      }

      &.activated {
        background-color: c.$background-alpha-active;
        color: c.$text;
      }
    }

    &.destructive {
      color: c.$destructive;

      &:not(:disabled, .activated) {
        &:hover {
          background-color: c.$destructive-light;
        }

        &:active {
          background-color: c.$destructive-light-hover;
        }
      }

      &.activated {
        background-color: c.$destructive-light-hover;
      }
    }
  }

  &.text {
    padding: 0;
    line-height: normal;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    cursor: pointer;
    text-decoration: underline solid;
    text-underline-offset: 0.15em;
    text-decoration-thickness: 0.1em;

    &[class]:disabled {
      cursor: not-allowed;
      color: c.$text-disabled;
      opacity: 0.5;
    }

    &.xsmall {
      height: 16px;

      @include typography.extra-small("semi-bold");
    }

    &.small {
      height: 18px;

      @include typography.small("semi-bold");
    }

    &.medium {
      height: 19px;

      @include typography.medium("semi-bold");
    }

    &.primary {
      color: c.$main;

      &:not(:disabled) {
        &:hover {
          color: c.$main-hover;
        }
      }
    }

    &.grey {
      color: c.$text-subtle;

      &:not(:disabled) {
        &:hover {
          color: c.$text;
        }
      }
    }

    &.destructive {
      color: c.$destructive;

      &:not(:disabled) {
        &:hover {
          color: c.$destructive-hover;
        }
      }
    }
  }
}
</style>
